import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FlexCenterWrapper } from "../../../../../../../components/DefaultNoDataWrapper";
import Flex from "../../../../../../../components/Flex";
import Text from "../../../../../../../components/Text";
import CustomTitle from "../../../../../../../components/app/CustomTitle";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import useSelectedPatient from "../../../../../../../hooks/local-storage/use-selected-patient";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { usePatientAssessmentCreate } from "../../../../../../../queries/patient-chart/assessment/use-assessment-post";
import { showToastOnApiCall } from "../../../../../../../utils/utils";
import AssessmentTableBuilder from "./AsessmentTableBuilder";
import { Assessment, ScreeningKeys } from "./constant-ass";
import useCurrentUser from "../../../../../../../hooks/local-storage/use-current-user";

interface NewAssessmentProps {
  defaultData: Record<ScreeningKeys, Assessment>;
  selectedScreening: ScreeningKeys | null;
  // setIsNewScreeningView?: (value: boolean) => void;
  onClose: () => void;
}

/**
 * Assessment Component
 * @returns Assessment Component use with AssessmentTableBuilder
 *
 * @author [Rajkumar Rathod]
 */
const NewAssessment = ({
  defaultData,
  selectedScreening,
  onClose,
}: // setIsNewScreeningView,
NewAssessmentProps) => {
  const [data, setData] = useState<Record<ScreeningKeys, Assessment> | null>(
    selectedScreening
      ? ({ [selectedScreening]: defaultData[selectedScreening] } as any)
      : null
  );

  const [payloadData, setPayloadData] =
    useState<Record<ScreeningKeys, Assessment>>();

  const [selectedPatient] = useSelectedPatient();

  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const [me] = useCurrentUser();
  const resetForm = () => {
    setPayloadData(undefined);
    setData(null);
  };

  const handleCreateAssessment = () => {
    const assessmentName = data?.[selectedScreening!]?.AssessmentName;
    const payload = {
      patientId: selectedPatient?.uuid,
      responses: payloadData,
      submittedBy: "PROVIDER",
      providerName: me?.firstName + " " + me?.lastName,
      providerRole: me?.role,
      assessmentName: assessmentName,
      reviewAt: new Date().toISOString(),
    };

    showToastOnApiCall({
      apiCall: createAssessment({ patientAssessment: payload }),
      showSnackbar,
      onSave: () => {
        onClose();
        queryClient.invalidateQueries({ queryKey: ["all-screening-list"] });
        queryClient.invalidateQueries({
          queryKey: ["graph-assessment-filled"],
        });
        queryClient.invalidateQueries({ queryKey: ["graph-assessment"] });
        resetForm();
        // setIsNewScreeningView?.(false);
      },
    });
  };

  const { mutateAsync: createAssessment } = usePatientAssessmentCreate();

  useEffect(() => {
    if (selectedScreening) {
      const newlySelectedScreening = {
        [selectedScreening]: defaultData?.[selectedScreening],
      } as any;

      setData((prev) => ({ ...prev, ...newlySelectedScreening }));
    }
  }, [selectedScreening, defaultData]);

  if (!data)
    return (
      <Flex width={"100%"} height={"90%"}>
        <FlexCenterWrapper>Please Select a Screening</FlexCenterWrapper>
      </Flex>
    );

  const currentSelectedAssessment = selectedScreening
    ? data?.[selectedScreening]
    : null;

  return (
    <Flex flexDirection={"column"} gap={1} width={"100%"} height={"100%"}>
      <Flex justifyContent={"space-between"} alignItems={"center"}>
        <CustomTitle> {currentSelectedAssessment?.AssessmentName}</CustomTitle>
        <Text fontWeight={"bold"} mr={1}>
          Total Score: {currentSelectedAssessment?.total_score ?? 0}
        </Text>
      </Flex>

      <Flex width={"100%"} height={"calc(100vh - 34.5vh)"} overflow={"scroll"}>
        <AssessmentTableBuilder
          sections={currentSelectedAssessment?.section ?? null}
          setData={setData}
          selectedScreening={selectedScreening}
          setPayloadData={setPayloadData}
        />
      </Flex>
      <Flex justifyContent={"end"} mr={1}>
        <StyledButtonV2
          label={"Finish"}
          disabled={Object.keys(payloadData ?? {}).length === 0}
          onClick={handleCreateAssessment}
        />
      </Flex>
    </Flex>
  );
};

export default NewAssessment;
